// Warum liegt dieser Beleg im Prüf-Pool? (Ticket #419, Björn 21.09.2026)
//
// Die Begründung stand bisher nur im Feld `notizen` — und das ist zugleich das freie
// Notizfeld der Sachbearbeitung. Wer dort etwas hineinschreibt („nochmal an ET
// weitergeleitet"), überschreibt die Erklärung des Imports. Der Grund wird deshalb aus
// den harten Feldern abgeleitet (grund, dokument_typ, fehlende Pflichtangaben) und nur
// ergänzend aus der Notiz gelesen, solange sie noch das Import-Muster trägt.

export interface PoolBeleg {
  grund?: string | null;
  notizen?: string | null;
  dokument_typ?: string | null;
  rechnungsnummer?: string | null;
  unternehmen?: string | null;
  netto?: number | string | null;
  brutto?: number | string | null;
  steuer?: number | string | null;
  datum?: string | null;
  zugeordnete_rechnung_id?: string | null;
}

export interface PoolErklaerung {
  /** Kurzform für Badge und Tabellenzeile. */
  titel: string;
  /** Ein Satz, der erklärt, was zu tun ist. */
  text: string;
}

const LEER = (v: unknown): boolean => v === null || v === undefined || String(v).trim() === '';

/** Pflichtangaben für eine gebuchte Rechnung — dieselbe Liste wie im Import. */
const PFLICHT: { feld: keyof PoolBeleg; name: string }[] = [
  { feld: 'rechnungsnummer', name: 'Rechnungsnummer' },
  { feld: 'unternehmen', name: 'Lieferant' },
  { feld: 'datum', name: 'Rechnungsdatum' },
  { feld: 'brutto', name: 'Bruttobetrag' },
  { feld: 'netto', name: 'Nettobetrag' },
  { feld: 'steuer', name: 'Steuerbetrag' },
];

export function fehlendePflichtangaben(b: PoolBeleg): string[] {
  return PFLICHT.filter(({ feld }) => LEER(b[feld])).map(({ name }) => name);
}

/** Liest „Confidence 0.62 unter Schwelle 0.80" aus der Import-Notiz, solange sie dort steht. */
export function unsichereErkennung(notizen?: string | null): boolean {
  return /confidence\s*[\d.,]+\s*unter\s*schwelle/i.test(notizen || '');
}

export function poolGrund(b: PoolBeleg): PoolErklaerung {
  const istMahnbeleg = b.dokument_typ === 'mahnung' || b.dokument_typ === 'zahlungserinnerung';

  if (istMahnbeleg) {
    if (b.zugeordnete_rechnung_id) {
      return {
        titel: 'Mahnung, bereits zugeordnet',
        text: 'Dieser Beleg ist eine Mahnung und hängt an der gemahnten Rechnung. Er wird nie als eigene Rechnung gebucht.',
      };
    }
    return {
      titel: 'Mahnung ohne gefundene Rechnung',
      text: 'Mahnungen werden nie automatisch gebucht — sonst stünde dieselbe Forderung zweimal im System. '
        + 'Die gemahnte Rechnung wurde nicht gefunden: entweder fehlt die Rechnungsnummer auf dem Schreiben, '
        + 'oder die ursprüngliche Rechnung liegt nicht in der App.',
    };
  }

  if (b.grund === 'rn_konflikt') {
    return {
      titel: 'Rechnungsnummer bereits vergeben',
      text: 'Eine Rechnung mit dieser Nummer ist schon erfasst. Prüfen Sie, ob es dieselbe Rechnung ist '
        + '(dann kann dieser Beleg weg) oder ob zwei Lieferanten dieselbe Nummer benutzen.',
    };
  }

  const fehlend = fehlendePflichtangaben(b);
  if (fehlend.length > 0) {
    return {
      titel: fehlend.length === 1 ? `${fehlend[0]} fehlt` : 'Pflichtangaben fehlen',
      text: `Aus dem Dokument liessen sich nicht alle Pflichtangaben lesen: ${fehlend.join(', ')}. `
        + 'Ergänzen Sie die Angaben von Hand, dann lässt sich der Beleg als Rechnung führen.',
    };
  }

  if (unsichereErkennung(b.notizen)) {
    return {
      titel: 'Erkennung war unsicher',
      text: 'Die Angaben wurden zwar alle gelesen, die Erkennung war sich aber nicht sicher genug. '
        + 'Bitte einmal gegen das Dokument prüfen.',
    };
  }

  return {
    titel: 'Zur Prüfung eingestellt',
    text: 'Dieser Beleg wurde nicht automatisch gebucht und wartet auf eine Entscheidung von Hand.',
  };
}
